import { useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  Alert, Badge, Button, ButtonGroup, Card, CardBody, Col, Form, FormGroup,
  Input, Label, Modal, ModalBody, ModalFooter, ModalHeader, Row,
} from 'reactstrap';
import { PageHeader } from '../../components/PageHeader';
import { DataTable } from '../../components/DataTable';
import { HasPermission } from '../../rbac/HasPermission';
import { useHasPermission } from '../../rbac/useHasPermission';
import { isMtffProxyEnabled } from '../../mtff/mtffClient';
import {
  useInstallPlugin,
  useMtffInstance,
  useMtffInstancePlugins,
  useMtffPluginCatalogue,
  useMtffPluginSettings,
  useSavePluginSettings,
  useUninstallPlugin,
  useUpdatePlugin,
} from './useMtff';

/**
 * MTFF Plugins pro Instance.
 *
 * Spec: MTFF/docs/ORBIT-INTEGRATION.md §3.3.
 * Install-Name muss im globalen Katalog stehen, sonst 400 validation_failed.
 */

export function MtffPluginsPage() {
  const { instanceId } = useParams();
  const enabled = isMtffProxyEnabled();
  const canWrite = useHasPermission({ perms: ['integrations.write'] });

  const instance = useMtffInstance(instanceId);
  const plugins = useMtffInstancePlugins(instanceId);
  const catalogue = useMtffPluginCatalogue();
  const install = useInstallPlugin();
  const update = useUpdatePlugin();
  const uninstall = useUninstallPlugin();

  const [settingsFor, setSettingsFor] = useState(null);
  const installed = plugins.data?.plugins ?? [];
  const installedNames = useMemo(() => new Set(installed.map((p) => p.name)), [installed]);

  if (!enabled) {
    return (
      <div>
        <PageHeader title="MTFF · Plugins" />
        <Alert color="warning" className="small">MTFF-Admin-Proxy nicht konfiguriert.</Alert>
      </div>
    );
  }

  const sorted = [...installed].sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0));

  return (
    <div>
      <PageHeader
        title={`MTFF · Plugins · ${instance.data?.tenantId ?? '…'}/${instance.data?.instanceId ?? '…'}`}
        subtitle={`${installed.length} installiert`}
        actions={
          <Button color="secondary" outline size="sm" tag={Link} to="/admin/settings/mtff/instances">
            ← Instances
          </Button>
        }
      />

      <Row className="g-3">
        <Col lg="8">
          <Card>
            <CardBody>
              <h6 className="text-uppercase small text-muted mb-2">Installiert</h6>
              <DataTable
                loading={plugins.isLoading}
                error={plugins.error}
                empty={!plugins.isLoading && installed.length === 0}
                emptyMessage="Keine Plugins installiert."
                onRetry={() => plugins.refetch()}
              >
                <thead>
                  <tr>
                    <th>Name</th>
                    <th style={{ width: 120 }}>Version</th>
                    <th style={{ width: 90 }}>Order</th>
                    <th style={{ width: 100 }}>Status</th>
                    <th style={{ width: 220 }}>Aktionen</th>
                  </tr>
                </thead>
                <tbody>
                  {sorted.map((p) => (
                    <tr key={p.name}>
                      <td className="small"><code>{p.name}</code></td>
                      <td className="small">{p.version ?? '—'}</td>
                      <td>
                        <HasPermission perms={['integrations.write']}>
                          <Input
                            type="number"
                            bsSize="sm"
                            defaultValue={p.sortOrder ?? 0}
                            onBlur={(e) => {
                              const val = Number(e.target.value);
                              if (val !== (p.sortOrder ?? 0) && canWrite) {
                                update.mutate({ instanceId, name: p.name, sortOrder: val });
                              }
                            }}
                            style={{ maxWidth: 70 }}
                            disabled={!canWrite}
                          />
                        </HasPermission>
                      </td>
                      <td>
                        <Badge color={p.enabled ? 'success' : 'secondary'} pill>
                          {p.enabled ? 'enabled' : 'disabled'}
                        </Badge>
                      </td>
                      <td>
                        <HasPermission perms={['integrations.write']}>
                          <ButtonGroup size="sm">
                            <Button
                              color={p.enabled ? 'warning' : 'success'}
                              outline
                              disabled={!canWrite || update.isPending}
                              onClick={() => update.mutate({ instanceId, name: p.name, enabled: !p.enabled })}
                            >
                              {p.enabled ? 'Disable' : 'Enable'}
                            </Button>
                            <Button color="info" outline onClick={() => setSettingsFor(p.name)} disabled={!canWrite}>
                              Settings
                            </Button>
                            <Button
                              color="danger"
                              outline
                              disabled={!canWrite || uninstall.isPending}
                              onClick={() => uninstall.mutate({ instanceId, name: p.name })}
                            >
                              ✕
                            </Button>
                          </ButtonGroup>
                        </HasPermission>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </DataTable>
            </CardBody>
          </Card>
        </Col>

        <Col lg="4">
          <Card>
            <CardBody>
              <h6 className="text-uppercase small text-muted mb-2">Katalog</h6>
              {catalogue.isLoading && <p className="small text-muted">Lade…</p>}
              <ul className="list-unstyled small mb-0">
                {(catalogue.data?.plugins ?? []).map((p) => {
                  const isInstalled = installedNames.has(p.name);
                  return (
                    <li key={p.name} className="mb-2">
                      <div className="d-flex justify-content-between align-items-center">
                        <span><code>{p.name}</code> <span className="text-muted">v{p.latestVersion}</span></span>
                        <HasPermission perms={['integrations.write']}>
                          <Button
                            size="sm"
                            color="primary"
                            outline
                            disabled={!canWrite || isInstalled || install.isPending}
                            onClick={() => install.mutate({ instanceId, name: p.name, version: p.latestVersion })}
                          >
                            {isInstalled ? '✓' : 'Install'}
                          </Button>
                        </HasPermission>
                      </div>
                      {p.description && <div className="text-muted">{p.description}</div>}
                    </li>
                  );
                })}
              </ul>
            </CardBody>
          </Card>
        </Col>
      </Row>

      {settingsFor && (
        <PluginSettingsModal
          key={settingsFor}
          instanceId={instanceId}
          name={settingsFor}
          onClose={() => setSettingsFor(null)}
        />
      )}
    </div>
  );
}

function PluginSettingsModal({ instanceId, name, onClose }) {
  const query = useMtffPluginSettings(instanceId, name);
  if (!query.data) {
    return (
      <Modal isOpen toggle={onClose} size="lg">
        <ModalHeader toggle={onClose}>Plugin-Settings · <code>{name}</code></ModalHeader>
        <ModalBody><p className="small text-muted">Lade…</p></ModalBody>
      </Modal>
    );
  }
  return (
    <PluginSettingsForm
      key={JSON.stringify(query.data.settings ?? {})}
      instanceId={instanceId}
      name={name}
      initial={query.data.settings ?? {}}
      onClose={onClose}
    />
  );
}

function PluginSettingsForm({ instanceId, name, initial, onClose }) {
  const save = useSavePluginSettings();
  const [text, setText] = useState(() => JSON.stringify(initial, null, 2));

  const onSubmit = async (e) => {
    e.preventDefault();
    let settings;
    try { settings = JSON.parse(text); }
    catch (err) {
      // eslint-disable-next-line no-alert
      alert('JSON ungültig: ' + err.message);
      return;
    }
    await save.mutateAsync({ instanceId, name, settings });
    onClose();
  };

  return (
    <Modal isOpen toggle={onClose} size="lg">
      <ModalHeader toggle={onClose}>
        Plugin-Settings · <code>{name}</code>
      </ModalHeader>
      <Form onSubmit={onSubmit}>
        <ModalBody>
          <FormGroup>
            <Label>Settings-JSON</Label>
            <Input
              type="textarea"
              rows={14}
              value={text}
              onChange={(e) => setText(e.target.value)}
              spellCheck={false}
              style={{ fontFamily: 'var(--bs-font-monospace)', fontSize: 13 }}
            />
          </FormGroup>
          <p className="small text-muted mb-0">
            PUT ersetzt das gesamte Settings-Objekt. Plugin-spezifische
            Validierung passiert backend-side; ungültige Felder kommen als
            400 zurück.
          </p>
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" outline onClick={onClose}>Abbrechen</Button>
          <Button color="primary" type="submit" disabled={save.isPending}>
            {save.isPending ? 'Speichere…' : 'Speichern'}
          </Button>
        </ModalFooter>
      </Form>
    </Modal>
  );
}
